import React, { useState, useEffect, useMemo } from 'react';
import api, { getImageUrl } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { FaTrash, FaSearch, FaCopy, FaSortAmountDown, FaSortAmountUp, FaCheckSquare, FaRegSquare } from 'react-icons/fa';

export default function AdminMedia() {
  useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const res = await api.get('/media');
      setFiles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Medya dosyalari yuklenirken hata olustu');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let result = files;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((f) => f.filename.toLowerCase().includes(q));
    }
    result = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'date') {
        cmp = new Date(a.created) - new Date(b.created);
      } else if (sortBy === 'size') {
        cmp = a.size - b.size;
      } else if (sortBy === 'name') {
        cmp = a.filename.localeCompare(b.filename);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [files, search, sortBy, sortDir]);

  const toggleSelect = (filename) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(filename)) {
        next.delete(filename);
      } else {
        next.add(filename);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((f) => f.filename)));
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`"${filename}" dosyasini silmek istediginize emin misiniz?`)) return;
    try {
      await api.delete(`/media/${encodeURIComponent(filename)}`);
      toast.success('Dosya silindi');
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(filename);
        return next;
      });
      fetchMedia();
    } catch (err) {
      toast.error('Silme sirasinda hata olustu');
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} dosyayi silmek istediginize emin misiniz?`)) return;
    try {
      await api.post('/media/bulk-delete', { filenames: Array.from(selected) });
      toast.success(`${selected.size} dosya silindi`);
      setSelected(new Set());
      fetchMedia();
    } catch (err) {
      toast.error('Toplu silme sirasinda hata olustu');
    }
  };

  const copyUrl = (filePath) => {
    const url = getImageUrl(filePath);
    const fullUrl = window.location.origin + url;
    navigator.clipboard.writeText(fullUrl).then(() => {
      toast.success('URL kopyalandi');
    }).catch(() => {
      toast.error('Kopyalama basarisiz');
    });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  if (loading) {
    return <div className="admin-loading">Yukleniyor...</div>;
  }

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Medya Kutuphanesi</h2>
          <span style={{ color: '#888', fontSize: '14px' }}>{files.length} dosya</span>
        </div>

        {/* Search & Sort Bar */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '20px', padding: '0 5px' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
            <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              placeholder="Dosya ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', padding: '8px 8px 8px 34px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '5px' }}>
            <button
              className={`btn btn-sm ${sortBy === 'date' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => toggleSort('date')}
            >
              Tarih {sortBy === 'date' && (sortDir === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}
            </button>
            <button
              className={`btn btn-sm ${sortBy === 'size' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => toggleSort('size')}
            >
              Boyut {sortBy === 'size' && (sortDir === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}
            </button>
            <button
              className={`btn btn-sm ${sortBy === 'name' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => toggleSort('name')}
            >
              Ad {sortBy === 'name' && (sortDir === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {filtered.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px', padding: '0 5px' }}>
            <button
              className="btn btn-sm btn-secondary"
              onClick={toggleSelectAll}
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              {selected.size === filtered.length ? <FaCheckSquare /> : <FaRegSquare />}
              {selected.size === filtered.length ? 'Secimi Kaldir' : 'Tumunu Sec'}
            </button>
            {selected.size > 0 && (
              <>
                <span style={{ fontSize: '13px', color: '#666' }}>{selected.size} secili</span>
                <button className="btn btn-sm btn-danger" onClick={handleBulkDelete}>
                  <FaTrash /> Secilenleri Sil
                </button>
              </>
            )}
          </div>
        )}

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="empty-state">
            <p>{search ? 'Aramayla eslesen dosya bulunamadi' : 'Henuz medya dosyasi yok'}</p>
          </div>
        ) : (
          <div className="image-grid">
            {filtered.map((file) => (
              <div
                className="image-grid-item"
                key={file.filename}
                style={{
                  outline: selected.has(file.filename) ? '3px solid #86CD91' : 'none',
                  borderRadius: '8px',
                  position: 'relative',
                }}
              >
                {/* Checkbox */}
                <div
                  onClick={(e) => { e.stopPropagation(); toggleSelect(file.filename); }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    zIndex: 3,
                    background: 'rgba(255,255,255,0.9)',
                    borderRadius: '4px',
                    padding: '2px 4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: selected.has(file.filename) ? '#86CD91' : '#999',
                  }}
                >
                  {selected.has(file.filename) ? <FaCheckSquare /> : <FaRegSquare />}
                </div>

                <img src={getImageUrl(file.path)} alt={file.filename} />

                <div className="overlay" style={{ flexDirection: 'column', gap: '8px', padding: '10px' }}>
                  <div style={{ fontSize: '11px', color: '#fff', textAlign: 'center', wordBreak: 'break-all' }}>
                    <strong>{file.filename}</strong>
                    <br />
                    {formatSize(file.size)}
                    {file.width && file.height && ` | ${file.width}x${file.height}`}
                    <br />
                    {formatDate(file.created)}
                  </div>
                  <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={(e) => { e.stopPropagation(); copyUrl(file.path); }}
                      title="URL Kopyala"
                    >
                      <FaCopy />
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={(e) => { e.stopPropagation(); handleDelete(file.filename); }}
                      title="Sil"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
